import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Card, Badge, Button } from '@/components/ui';
import { ClipboardList, Settings, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM } from '@/lib/motion';
import type { Role } from '@/types';
import { billingBadgeBase, billingBadgeVariant } from './roleFormStyles';
import { RoleList } from './RoleList';
import { DayRateConfigForm, type DayRateConfigFormProps } from '@/components/config_forms/roles/DayRateConfigForm';
import { focusField } from '@/utils/focus';
import { calculateHalfDayRate } from '@/utils/calculateHalfDayRate';

export interface RateSheetConfigFormProps {
  roles: Role[];
  onRemoveRole: (index: number) => void;
  onRolesChange: (roles: Role[]) => void;
  onEditingChange?: (isEditing: boolean) => void;
  isCustomized?: boolean;
  onRevertAll?: () => void;
  title?: string;
  description?: string;
  context?: 'user' | 'client';
  dayRateConfig?: DayRateConfigFormProps;
  isUserProfile?: boolean; // New prop to identify UserRolesConfig context
  dayRateSettingsCollapsible?: boolean;
}

const RateSheetConfigFormComponent: React.FC<RateSheetConfigFormProps> = ({
  roles,
  onRemoveRole,
  onRolesChange,
  onEditingChange,
  isCustomized,
  onRevertAll,
  title = "Rate Sheet",
  description = "Add roles you work as and set each one's billing structure and rate.",
  context = 'user',
  dayRateConfig,
  isUserProfile = false,
  dayRateSettingsCollapsible = true,
}) => {
  const [hasDayRateFromGrid, setHasDayRateFromGrid] = useState(false);
  const [hasHalfDayBillingEnabled, setHasHalfDayBillingEnabled] = useState(
    () => roles.some(r => r.billingStructure === 'day_rate' && r.allowHalfDay)
  );
  
  // New logic for contextual billing structure settings
  const dayRateConfigRef = useRef<HTMLDivElement>(null);
  const dayRateHeaderRef = useRef<HTMLButtonElement>(null);
  const [dayRateSettingsExpanded, setDayRateSettingsExpanded] = useState(!dayRateSettingsCollapsible);

  // Scroll the day rate header to top of viewport during collapse/expand animation
  const scrollDayRateIntoView = useCallback((block: ScrollLogicalPosition = 'start') => {
    requestAnimationFrame(() => {
      const el = dayRateHeaderRef.current;
      if (!el) return;
      let active = true;
      const track = () => {
        if (!active) return;
        el.scrollIntoView({ block, behavior: 'auto' });
        requestAnimationFrame(track);
      };
      requestAnimationFrame(track);
      setTimeout(() => { active = false; }, 350);
    });
  }, []);

  const handleDayRateToggle = useCallback(() => {
    setDayRateSettingsExpanded(prev => {
      const opening = !prev;
      scrollDayRateIntoView(opening ? 'start' : 'nearest');
      return opening;
    });
  }, [scrollDayRateIntoView]);
  const [rateCardOpen, setRateCardOpen] = useState(true);
  const rateCardHeaderRef = useRef<HTMLButtonElement>(null);
  const prevRolesRef = useRef<Role[]>(roles);
  const hasHydratedRolesRef = useRef(false);
  const shouldShowDayRateSettings =
    !!dayRateConfig && (roles.some(r => r.billingStructure === 'day_rate') || hasDayRateFromGrid);
  
  const prevShouldShowDayRateSettings = useRef<boolean>();
  const prevHadDayRateRoles = useRef<boolean | null>(null);
  const lastUserRolesChangeRef = useRef<number>(0);
  const prevHadHalfDayRoles = useRef<boolean | null>(null);
  const prevHadHalfDayRateConfigured = useRef<boolean | null>(null);

  // NEW:
  const pendingFullDayAutoFocusRef = useRef<boolean>(false);
  const pendingHalfDayFocusCleanupRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    const wasVisible = prevShouldShowDayRateSettings.current;
    const isVisible = shouldShowDayRateSettings;

    // This detects the initial async load of roles, preventing animation on page load.
    const isInitialLoadTransition = prevRolesRef.current.length === 0 && roles.length > 0;

    let timer: ReturnType<typeof setTimeout> | undefined;
    // Auto-scroll to reveal day rate settings when they first appear after a user action
    if (!wasVisible && isVisible && !isInitialLoadTransition) {
      // Wait a frame for the AnimatePresence to mount, then track the expand animation
      timer = setTimeout(() => scrollDayRateIntoView('start'), 50);
    }

    // update refs for next render
    prevRolesRef.current = roles;
    prevShouldShowDayRateSettings.current = isVisible;

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollDayRateIntoView is a stable useCallback; including it would not change behavior but clutters the dep list
  }, [roles, shouldShowDayRateSettings]);

  useEffect(() => {
    const hasDayRateNow = roles.some(r => r.billingStructure === 'day_rate');

    // Skip the first roles hydration after mount/load
    if (!hasHydratedRolesRef.current) {
      hasHydratedRolesRef.current = true;
      prevHadDayRateRoles.current = hasDayRateNow; // keep in sync
      return;
    }

    const hadDayRate = prevHadDayRateRoles.current;

    let scrollTimer: ReturnType<typeof setTimeout> | undefined;
    let focusTimer: ReturnType<typeof setTimeout> | undefined;

    // Only trigger when we transition from no day-rate roles to having one
    // (i.e., after adding/saving a role that becomes the first Day Rate role)
    if (
      hadDayRate === false &&
      hasDayRateNow &&
      lastUserRolesChangeRef.current &&
      Date.now() - lastUserRolesChangeRef.current < 2000 // only if a recent user change
    ) {
      // Auto-expand the day rate settings section and scroll into view
      setDayRateSettingsExpanded(true);
      scrollTimer = setTimeout(() => scrollDayRateIntoView('start'), 50);

      // Skip if minimumBillableHours already has a value
      if (!dayRateConfig?.minimumBillableHours) {
        pendingFullDayAutoFocusRef.current = true;
        focusTimer = setTimeout(() => {
          // Don't steal focus if the user is interacting with the role list
          const roleListEl = document.querySelector('[data-role-list]');
          if (roleListEl?.contains(document.activeElement)) {
            pendingFullDayAutoFocusRef.current = false;
            return;
          }
          focusField('minimumBillableHours');
          pendingFullDayAutoFocusRef.current = false;
        }, 500);
      }
    }

    prevHadDayRateRoles.current = hasDayRateNow;

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(focusTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollDayRateIntoView is stable (useCallback with no deps); dayRateConfig?.minimumBillableHours is read inside a conditional guard and intentionally omitted to avoid re-firing on every config change
  }, [roles]);

  useEffect(() => {
    const hasHalfDayNow = roles.some(r => r.billingStructure === 'day_rate' && r.allowHalfDay);
    const hasHalfDayRateConfigured = roles.some(
      r => r.billingStructure === 'day_rate' && r.allowHalfDay && calculateHalfDayRate(r) !== null
    );

    // Clear any pending half-day focus hooks on re-run/unmount
    pendingHalfDayFocusCleanupRef.current?.();
    pendingHalfDayFocusCleanupRef.current = null;

    // Skip the first roles hydration after mount/load
    if (!hasHydratedRolesRef.current) {
      prevHadHalfDayRoles.current = hasHalfDayNow;
      prevHadHalfDayRateConfigured.current = hasHalfDayRateConfigured;
      return;
    }

    // Auto-focus half-day threshold only after a half-day rate is first configured,
    // and only if the threshold isn't already set AND minimum billable hours is already set
    // (if both are empty, minimumBillableHours takes priority and halfDayHours will be
    // focused after the user fills it in via the onChange auto-fill flow)
    const hadRateConfigured = prevHadHalfDayRateConfigured.current;
    let scrollTimer: ReturnType<typeof setTimeout> | undefined;
    let focusTimer: ReturnType<typeof setTimeout> | undefined;

    if (
      hadRateConfigured === false &&
      hasHalfDayRateConfigured &&
      lastUserRolesChangeRef.current &&
      Date.now() - lastUserRolesChangeRef.current < 2000 &&
      !dayRateConfig?.halfDayHours &&
      dayRateConfig?.minimumBillableHours
    ) {
      // Auto-expand the day rate settings section and scroll into view
      setDayRateSettingsExpanded(true);
      scrollTimer = setTimeout(() => scrollDayRateIntoView('start'), 50);
      focusTimer = setTimeout(() => focusField('halfDayHours'), 400);
    }

    prevHadHalfDayRoles.current = hasHalfDayNow;
    prevHadHalfDayRateConfigured.current = hasHalfDayRateConfigured;

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(focusTimer);
      pendingHalfDayFocusCleanupRef.current?.();
      pendingHalfDayFocusCleanupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scrollDayRateIntoView is stable; dayRateConfig fields are read inside conditional guards and intentionally omitted to only fire on role changes
  }, [roles]);

  // Create wrapper to track user-driven role changes
  const handleRolesChange = React.useCallback((updated: Role[]) => {
    lastUserRolesChangeRef.current = Date.now();
    onRolesChange(updated);
  }, [onRolesChange]);

  return (
    <>
      {isUserProfile ? (
        <div id="roles-step" className="animate-fade-in w-full">
          <div className="space-y-4 md:space-y-6 lg:max-w-3xl">
            <div>
              <button
                ref={rateCardHeaderRef}
                type="button"
                onClick={() => setRateCardOpen(prev => !prev)}
                aria-expanded={rateCardOpen}
                className="w-full flex items-center justify-between py-1 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              >
                <div className="flex items-center gap-3">
                  <ClipboardList aria-hidden="true" className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <h2 className="font-medium text-base text-foreground">{title}</h2>
                  {isCustomized && <Badge variant="outline">{context === 'client' ? 'Client Override' : 'Profile Override'}</Badge>}
                </div>
                <ChevronDown aria-hidden="true" className={cn(
                  "w-4 h-4 text-muted-foreground transition-transform duration-200",
                  rateCardOpen && "rotate-180"
                )} />
              </button>
              {rateCardOpen && (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              )}
              {rateCardOpen && isCustomized && onRevertAll && (
                <Button
                  variant="link"
                  onClick={onRevertAll}
                  className="text-sm h-auto p-0 mt-1"
                  type="button"
                  aria-label="Revert entire rate sheet to profile defaults"
                >
                  Revert Rate Sheet to Profile Defaults
                </Button>
              )}
            </div>

            <AnimatedCollapse open={rateCardOpen}>
              <div className="border border-border rounded bg-card dark:bg-transparent shadow-sm">
                {/* Roles header bar */}
                <div className="flex items-center px-3 py-3 min-h-[44px] bg-muted border-b-2 border-b-border">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ClipboardList aria-hidden="true" className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground">
                      Roles
                    </h3>
                    {(() => {
                      const withBilling = roles.filter((r): r is Role & { billingStructure: 'day_rate' | 'hourly' } => r.billingStructure === 'day_rate' || r.billingStructure === 'hourly');
                      if (!withBilling.length) return null;
                      const maxVisible = 3;
                      const visible = withBilling.slice(0, maxVisible);
                      const overflow = withBilling.length - maxVisible;
                      return (
                        <>
                          {visible.map((r) => (
                            <span key={r.name} className={cn(billingBadgeBase, billingBadgeVariant(r.billingStructure))}>
                              {r.name}
                            </span>
                          ))}
                          {overflow > 0 && (
                            <span className={cn(billingBadgeBase, 'text-muted-foreground border border-border')}>
                              +{overflow}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {/* Roles content */}
                <div className="px-3 pt-3 pb-3">
                  <RoleList
                    roles={roles}
                    onRolesChange={handleRolesChange}
                    onRemove={onRemoveRole}
                    onHasDayRateChange={setHasDayRateFromGrid}
                    onHasHalfDayChange={setHasHalfDayBillingEnabled}
                    onEditingChange={onEditingChange}
                  />
                </div>

                {/* Day Rate Settings — accordion section */}
                <AnimatedCollapse open={shouldShowDayRateSettings}>
                      <div className="border-t border-border/40 mt-3">
                        {(() => {
                          if (!dayRateConfig) return null;
                          const isOpen = !dayRateSettingsCollapsible || dayRateSettingsExpanded;
                          const overrideBadgeLabel = dayRateConfig.context === 'client' ? 'Client Override' : 'Profile Override';

                          // Build summary tokens for collapsed header
                          const dayRateSummaryTokens: { label: string; value: string }[] = [];
                          if (dayRateConfig.minimumBillableHours != null) {
                            dayRateSummaryTokens.push({ label: 'Full Day', value: `${dayRateConfig.minimumBillableHours}h` });
                          }
                          if (dayRateConfig.halfDayHours != null && !dayRateConfig.isHalfDayConfigDisabled) {
                            dayRateSummaryTokens.push({ label: 'Half-Day', value: `${dayRateConfig.halfDayHours}h` });
                          }

                          const headerContent = (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Settings aria-hidden="true" className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span role="heading" aria-level={3} className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground">
                                {dayRateConfig.title}
                              </span>
                              {dayRateConfig.isCustomized && <Badge variant="outline">{overrideBadgeLabel}</Badge>}
                              <AnimatePresence mode="popLayout">
                                {dayRateSummaryTokens.map((t) => (
                                  <motion.span
                                    key={t.label}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.85 }}
                                    transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                                    className={cn(billingBadgeBase, billingBadgeVariant('day_rate'), 'inline-flex items-center gap-1 !bg-card dark:!bg-primary/10')}
                                  >
                                    {t.label}:{' '}
                                    <AnimatePresence mode="popLayout" initial={false}>
                                      <motion.span
                                        key={t.value}
                                        initial={{ opacity: 0, y: -4 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 4 }}
                                        transition={{ duration: DURATION_FAST, ease: EASE_EXPO_OUT }}
                                      >
                                        {t.value}
                                      </motion.span>
                                    </AnimatePresence>
                                  </motion.span>
                                ))}
                              </AnimatePresence>
                            </div>
                          );

                          return dayRateSettingsCollapsible ? (
                            <button
                              ref={dayRateHeaderRef}
                              type="button"
                              onClick={handleDayRateToggle}
                              aria-expanded={dayRateSettingsExpanded}
                              aria-label="Toggle day rate settings"
                              className={cn(
                                'w-full flex items-center justify-between px-3 py-3 min-h-[44px]',
                                'bg-muted hover:bg-muted/80 transition-colors cursor-pointer',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                                isOpen && 'border-b-2 border-b-border'
                              )}
                            >
                              {headerContent}
                              <ChevronDown aria-hidden="true" className={cn(
                                "w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0",
                                isOpen && "rotate-180"
                              )} />
                            </button>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-3 min-h-[44px] bg-muted border-b-2 border-b-border">
                              {headerContent}
                            </div>
                          );
                        })()}

                        <AnimatedCollapse open={!dayRateSettingsCollapsible || dayRateSettingsExpanded}>
                          {dayRateConfig && (
                          <div ref={dayRateConfigRef} className="px-3 pt-3 pb-3">
                            <p className="text-[10px] sm:text-[12px] text-muted-foreground mb-3">
                              {dayRateConfig.description} <span className="text-muted-foreground/70">Applies to all Day Rate roles above.</span>
                            </p>
                            <DayRateConfigForm
                              {...dayRateConfig}
                              integrated
                              isMinimumBillableHoursDisabled={!!dayRateConfig.isMinimumBillableHoursDisabled && !hasDayRateFromGrid}
                              isHalfDayConfigDisabled={!!dayRateConfig.isHalfDayConfigDisabled && (!hasDayRateFromGrid || !hasHalfDayBillingEnabled)}
                            />
                          </div>
                          )}
                        </AnimatedCollapse>
                      </div>
                </AnimatedCollapse>
              </div>
            </AnimatedCollapse>
          </div>
        </div>
      ) : (
        <Card id="roles-step" className="animate-fade-in p-3 md:p-6">
          <div className="space-y-4 md:space-y-6">
            <div className="space-y-1 text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20">
                  <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">{title}</h3>
                {isCustomized && <Badge variant="outline">{context === 'client' ? 'Client Override' : 'Profile Override'}</Badge>}
              </div>
              <p className="text-sm text-muted-foreground px-2 md:px-4 max-w-2xl mx-auto leading-relaxed">
                {description}
              </p>
              {isCustomized && onRevertAll && (
                <Button variant="link" onClick={onRevertAll} className="text-sm h-auto p-0">
                  Revert Rate Sheet to Profile Defaults
                </Button>
              )}
            </div>

            <div>
              <div className="mt-3">
                <RoleList
                  roles={roles}
                  onRolesChange={handleRolesChange}
                  onRemove={onRemoveRole}
                  onHasDayRateChange={setHasDayRateFromGrid}
                  onHasHalfDayChange={setHasHalfDayBillingEnabled}
                  onEditingChange={onEditingChange}
                />
              </div>

              <AnimatedCollapse open={shouldShowDayRateSettings} className="mt-6">
                {dayRateConfig && (<>
                    <div className="space-y-1 mb-3">
                      {dayRateSettingsCollapsible ? (
                        <button
                          type="button"
                          className="flex items-center justify-between w-full text-left group"
                          onClick={() => setDayRateSettingsExpanded(prev => !prev)}
                          aria-expanded={dayRateSettingsExpanded}
                        >
                          <div className="flex items-center gap-2">
                            <Settings className="w-4 h-4 text-muted-foreground" />
                            <h4 className="text-sm font-medium text-foreground">{dayRateConfig.title}</h4>
                            {dayRateConfig.isCustomized && <Badge variant="outline">{dayRateConfig.context === 'client' ? 'Client Override' : 'Profile Override'}</Badge>}
                          </div>
                          <ChevronDown className={cn(
                            "w-4 h-4 text-muted-foreground transition-transform duration-200 sm:hidden",
                            dayRateSettingsExpanded && "rotate-180"
                          )} />
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Settings className="w-4 h-4 text-muted-foreground" />
                          <h4 className="text-sm font-medium text-foreground">{dayRateConfig.title}</h4>
                          {dayRateConfig.isCustomized && <Badge variant="outline">{dayRateConfig.context === 'client' ? 'Client Override' : 'Profile Override'}</Badge>}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground">{dayRateConfig.description} <span className="text-muted-foreground/70">Applies to all Day Rate roles above.</span></p>
                    </div>
                    <AnimatedCollapse open={dayRateSettingsExpanded}>
                          <Card
                            ref={dayRateConfigRef}
                            variant="outlined"
                            className="p-5 bg-transparent"
                          >
                            <DayRateConfigForm
                              {...dayRateConfig}
                              integrated
                              isMinimumBillableHoursDisabled={!!dayRateConfig.isMinimumBillableHoursDisabled && !hasDayRateFromGrid}
                              isHalfDayConfigDisabled={!!dayRateConfig.isHalfDayConfigDisabled && (!hasDayRateFromGrid || !hasHalfDayBillingEnabled)}
                            />
                          </Card>
                    </AnimatedCollapse>
                </>)}
              </AnimatedCollapse>

            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export const RateSheetConfigForm = React.memo(RateSheetConfigFormComponent);
RateSheetConfigForm.displayName = 'RateSheetConfigForm';
